import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { announceCartLinesUpdate, settleWithFetchedCart, announceCartError } from '@theme/standard-cart-events';
import { moneyFromCents, positionHotspotTooltip } from '@theme/utilities';
import '@theme/bundle-product-card';

/**
 * A curated fixed bundle: sums the selected variants of its rows and bulk-adds them to the cart.
 * Reuses <bundle-product-card> (registered by bundle-builder.js) for per-row variant resolution.
 */
class ProductBundle extends Component {
  #cards = [];
  #hotspots = [];
  #hoverQuery = window.matchMedia('(hover: hover)');

  connectedCallback() {
    super.connectedCallback();
    this.#cards = Array.from(this.querySelectorAll('bundle-product-card'));
    this.#setupHotspots();
    this.addEventListener('bundle-card:updated', this.#onVariantChange, { signal: this.abortController.signal });
    customElements.whenDefined('bundle-product-card').then(() => this.renderTotal());
  }

  #setupHotspots() {
    const imageCol = this.querySelector('.product-bundle__image-col');
    const { signal } = this.abortController;
    this.#cards.forEach((card, index) => {
      const item = card.closest('.product-bundle__item');
      const hotspot = item?.querySelector('.product-bundle__hotspot');
      if (!hotspot || !imageCol) return;
      const quickView = card.querySelector('quick-view-component');
      hotspot.dataset.index = String(index);
      hotspot.classList.remove('hidden');
      imageCol.appendChild(hotspot);
      hotspot.addEventListener('pointerenter', () => {
        this.#activate(index, true);
        quickView?.dispatchEvent(new PointerEvent('pointerenter'));
      }, { signal });
      hotspot.addEventListener('pointerleave', (event) => {
        if (hotspot.contains(event.relatedTarget)) return;
        this.#clearActive();
        quickView?.dispatchEvent(new PointerEvent('pointerleave'));
      }, { signal });
      hotspot.addEventListener('click', () => this.#openProduct(this.#cards[index]), { signal });
      item.addEventListener('mouseenter', () => this.#activate(index), { signal });
      item.addEventListener('mouseleave', () => this.#clearActive(), { signal });
      this.#hotspots.push(hotspot);
    });
  }

  /** @type {boolean} Whether the device can hover (skip hover UI on touch mobile/tablet). */
  get #canHover() {
    return this.#hoverQuery.matches;
  }

  /**
   * Highlights the hotspot (and its tooltip) for the given index. When `dimOthers` is set (hover
   * started on the image hotspot), also fades the sibling rows via the host's is-hovering state.
   */
  #activate(index, dimOthers = false) {
    if (!this.#canHover) return;
    this.#clearActive();
    if (dimOthers) {
      this.classList.add('is-hovering');
      this.#cards[index]?.closest('.product-bundle__item')?.classList.add('is-active');
    }
    this.#hotspots[index]?.classList.add('is-active');
    this.#openTooltip(index);
  }

  /** Opens the row's tooltip, kept inside the image column. */
  #openTooltip(index) {
    const hotspot = this.#hotspots[index];
    if (!hotspot) return;
    positionHotspotTooltip(hotspot, this.querySelector('.product-bundle__image-col'));
    hotspot.classList.add('is-tooltip-open');
  }

  #clearActive() {
    this.classList.remove('is-hovering');
    this.#hotspots.forEach((hotspot) => hotspot.classList.remove('is-active', 'is-tooltip-open'));
    this.#cards.forEach((card) => card.closest('.product-bundle__item')?.classList.remove('is-active'));
  }

  #openProduct(card) {
    card?.querySelector('quick-view-component .quick-view__button')?.click();
  }

  #onVariantChange = () => this.renderTotal();

  /**
   * The variant a row contributes to the bundle, or null when it contributes none. Subscription-only
   * products are excluded: the bulk add carries no selling plan, so Shopify would reject the whole
   * call — and the advertised total has to leave them out for the same reason.
   *
   * @param {Element & { selectedVariant?: object, requiresSellingPlan?: boolean }} card
   * @returns {object | null}
   */
  #addableVariant(card) {
    if (card.requiresSellingPlan) return null;
    const variant = card.selectedVariant;
    return variant && variant.available ? variant : null;
  }

  /**
   * Recomputes the running total, the saving badge, and the add-all button's disabled state.
   * The advertised total is the discounted one — subtotal minus the saving — matching how the
   * bundle builder's summary reports it.
   */
  renderTotal() {
    const total = this.querySelector('[ref="total"]');
    const savingEl = this.querySelector('[ref="saving"]');
    const btn = this.querySelector('[ref="addButton"]');
    let subtotal = 0;
    let addable = 0;
    let listed = 0;
    for (const card of this.#cards) {
      if (!card.dataset.productId) continue;
      listed += 1;
      const variant = this.#addableVariant(card);
      if (variant) {
        subtotal += variant.price;
        addable += 1;
      }
    }
    const saving = savingEl ? this.#saving(savingEl, subtotal) : 0;
    if (total) {
      total.textContent = moneyFromCents(
        Math.max(0, subtotal - saving),
        this.dataset.moneyWithCurrencyFormat || this.dataset.moneyFormat
      );
    }
    if (savingEl) {
      savingEl.hidden = saving <= 0;
      if (saving > 0) {
        const amount = moneyFromCents(saving, this.dataset.moneyFormat);
        savingEl.textContent = (savingEl.dataset.template || '[amount]').replace('[amount]', amount);
      }
    }
    if (btn) btn.disabled = addable === 0 || addable < listed;
  }

  /**
   * The saving for the block's configured discount, in cents, never above the subtotal. A fixed
   * amount is authored in the shop currency, so it is converted the same way the bundle builder
   * converts its tier amounts.
   *
   * @param {HTMLElement} savingEl
   * @param {number} subtotal
   * @returns {number}
   */
  #saving(savingEl, subtotal) {
    const value = Number(savingEl.dataset.discountValue) || 0;
    if (subtotal <= 0 || value <= 0) return 0;
    if (savingEl.dataset.discountType === 'fixed_amount') {
      const rate = Number(window.Shopify?.currency?.rate) || 1;
      return Math.min(subtotal, Math.round(value * 100 * rate));
    }
    return Math.round((subtotal * value) / 100);
  }

  /** Bulk-adds every listed product's selected available variant. Bound to on:click="product-bundle/addAllToCart". */
  async addAllToCart() {
    const btn = this.querySelector('[ref="addButton"]');
    const items = this.#cards
      .map((card) => this.#addableVariant(card))
      .filter(Boolean)
      .map((variant) => ({ id: Number(variant.id), quantity: 1 }));
    if (items.length === 0) return;
    this.#hideError();
    btn?.classList.add('btn--loading');
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const announcement = announceCartLinesUpdate(this, {
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      if (data.status) {
        // Shopify puts a generic "Cart Error" in message and the actual reason in description.
        const reason = data.description || data.message;
        announcement.reject(new Error(reason));
        announceCartError(this, { error: reason, code: 'INVALID', detail: { description: data.description, errors: data.errors } });
        document.dispatchEvent(new CartErrorEvent(this.dataset.sectionId, reason));
        this.#showError(reason);
        return;
      }
      document.dispatchEvent(
        new CartAddEvent({}, this.dataset.sectionId, {
          source: 'product-bundle',
          sections: data.sections,
        })
      );

      settleWithFetchedCart(announcement, { source: 'product-bundle', sections: data.sections });
    } catch (error) {
      announcement.reject(error);
      announceCartError(this, { error: error?.message ?? String(error), code: 'SERVICE_UNAVAILABLE' });
      document.dispatchEvent(new CartErrorEvent(this.dataset.sectionId, String(error)));
      this.#showError(error.message ?? String(error));
    } finally {
      btn?.classList.remove('btn--loading');
    }
  }

  #showError(message) {
    const text = this.querySelector('[ref="errorText"]');
    const live = this.querySelector('[ref="liveRegion"]');
    if (text) text.textContent = message;
    if (live) live.textContent = message;
    this.querySelector('[ref="error"]')?.classList.remove('hidden');
    this.querySelector('[ref="addButton"]')?.classList.add('has-error');
  }

  #hideError() {
    this.querySelector('[ref="error"]')?.classList.add('hidden');
    this.querySelector('[ref="addButton"]')?.classList.remove('has-error');
    const live = this.querySelector('[ref="liveRegion"]');
    if (live) live.textContent = '';
  }
}

if (!customElements.get('product-bundle')) {
  customElements.define('product-bundle', ProductBundle);
}
